// The detail of one PBJ activity (a node of the dataset), shared by the PBJ
// project checklist and Alur PBJ (reference detailModal): pelaku,
// penjelasan, output, otoritas penetapan, simpul selanjutnya (each id named
// by its activity when the caller knows it, with the branch label of a
// decision), and the regulations with their JDIH links plus the verification
// note on Perlem LKPP 3/2026.
import type { PbjFlowNode } from "../api";
import { Glyph } from "../ui/icons";

type NodeLike = Pick<PbjFlowNode, "actor" | "explanation" | "output" | "authority" | "regulations"> & { next?: string };

/** One branch of "Simpul Selanjutnya": "05_KONTRAK_06 (Belum selesai)" is { id, label }. */
export interface NextStep {
  id: string;
  label: string | null;
}

/** The dataset's "a ; b (label)" next-node text, in order. */
export function parseNext(text: string | undefined): NextStep[] {
  return (text ?? "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const m = /^(\S+)\s*(?:\((.*)\))?$/.exec(part);
      return m ? { id: m[1]!, label: m[2]?.trim() || null } : { id: part, label: null };
    });
}

/** The regulations cite Perlem LKPP 3/2026: it is checked against JDIH before use (reference legalLinks). */
export function needsVerification(node: Pick<NodeLike, "regulations">): boolean {
  return node.regulations.some((r) => /3\/2026/.test(r.title));
}

export interface NodeFactsProps {
  node: NodeLike;
  showActor?: boolean;
  /** The activity name of a node id, for Simpul selanjutnya; an unknown id shows as it is. */
  nameOf?: (id: string) => string | undefined;
}

export function NodeFacts({ node, showActor = true, nameOf }: NodeFactsProps) {
  const next = parseNext(node.next);
  return (
    <dl className="pbj-facts">
      {showActor ? (
        <div className="pbj-fact">
          <dt>Pelaku</dt>
          <dd>{node.actor || "Belum ditentukan"}</dd>
        </div>
      ) : null}
      {node.explanation ? (
        <div className="pbj-fact">
          <dt>Penjelasan</dt>
          <dd>{node.explanation}</dd>
        </div>
      ) : null}
      {node.output ? (
        <div className="pbj-fact">
          <dt>Output</dt>
          <dd>{node.output}</dd>
        </div>
      ) : null}
      {node.authority ? (
        <div className="pbj-fact">
          <dt>Otoritas penetapan</dt>
          <dd>{node.authority}</dd>
        </div>
      ) : null}
      {next.length > 0 ? (
        <div className="pbj-fact">
          <dt>Simpul selanjutnya</dt>
          <dd>
            <ul className="pbj-regs pbj-next-steps">
              {next.map((s, i) => {
                const name = nameOf?.(s.id);
                return (
                  <li key={`${s.id}-${i}`}>
                    {name ?? s.id}
                    {s.label ? <span className="pbj-next-label"> ({s.label})</span> : null}
                  </li>
                );
              })}
            </ul>
          </dd>
        </div>
      ) : null}
      {node.regulations.length > 0 ? (
        <div className="pbj-fact">
          <dt>Dasar hukum</dt>
          <dd>
            <ul className="pbj-regs">
              {node.regulations.map((r) => (
                <li key={r.title}>
                  {r.url ? (
                    <a className="pbj-reg-link" href={r.url} target="_blank" rel="noopener noreferrer">
                      <span>{r.title}</span>
                      <Glyph name="link" size={16} />
                    </a>
                  ) : (
                    <span>{r.title}</span>
                  )}
                </li>
              ))}
            </ul>
            {needsVerification(node) ? (
              <p className="pbj-verify">
                <Glyph name="infoCircle" size={16} />
                <span>
                  Perlem LKPP 3/2026 perlu diverifikasi di JDIH. Gunakan Perlem LKPP 12/2021 jo. 4/2024 sebagai pedoman yang terverifikasi, lalu periksa penerapan dan perubahannya pada tanggal pengadaan.
                </span>
              </p>
            ) : null}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
